<template>
  <div>
    <card>
      <template v-slot:title>
        {{ $t('participants.person') }} {{ index+1 }} ({{ translate(getParticipantType(participant.participant_id), 'name') }})
      </template>
      <template v-slot:body>
        <ria-input-action
          v-if="!isAnonymous && Object.keys(customer).length > 0 && customer.people.length > 0"
          :required="false"
          :label="$t('participants.chooseskier')"
          name="id"
          icon="user"
          :value="getFullname(personSelected)"
          :action="openParticipantPicker"
          @change="changeValue" />
        <ria-picker
          v-if="isParticipantPickerVisible && customer.people.length > 0"
          :label="$t('participants.chooseskier')"
          :options="availablePeopleOptions"
          @close="closeParticipantPicker"
          @select="changeParticipant" />
        <ria-input
          name="firstname"
          :label="$t('participants.name')"
          validation="required"
          type="text"
          :value="participant.firstname"
          ref="firstname"
          @change="changeValue"/>
        <ria-input
          name="lastname"
          :label="$t('participants.lastname')"
          validation="required"
          type="text"
          :value="participant.lastname"
          ref="lastname"
          @change="changeValue"/>
        <ria-input
          name="birthdate"
          :label="$t('participants.birthdate.title') + (isSafariBrowser?' (YYYY-MM-DD)':'')"
          :validation="birthdateValidation"
          :help="$t('participants.birthdate.helper')"
          type="datewithplaceholder"
          ref="birthdate"
          :value="participant.birthdate"
          @change="changeValue" />
        <ria-input-action
          v-if="selectedProduct.requirements.photo"
          :label="$t('participants.photo.photo')"
          name="photo"
          icon="photo"
          :additional-value="participant.photo"
          ref="photo"
          @change="changeValue"
          :action="goToPhoto" />
        <ria-input-action
          v-if="selectedProduct.requirements.keycard"
          :label="$t('participants.keycard.keycard')"
          name="keycard"
          icon="keycard"
          :value="keycard_number"
          :additional-value="participant.keycard.new ? $t('participants.keycard.pickup') : ''"
          ref="keycard"
          @change="changeValue"
          :action="goToKeycard"/>
        <ria-checkbox-extra
          v-for="(extra) in availableExtras"
          :key="extra.id"
          :label="translate(extra, 'name')"
          :price="extraPrice(extra.id, participant.participant_id)"
          :extraId="extra.id"
          :enabled="enabledExtra(extra.id)"
          @change="changeExtra"/>
      </template>
    </card>
    <separator/>
  </div>
</template>

<script>
  import { mapGetters, mapMutations } from "vuex";
  import Card from "../common/card";
  import RiaInput from "./elements/ria-input";
  import RiaInputAction from "./elements/ria-input-action";
  import RiaCheckboxExtra from "./elements/ria-checkbox-extra";
  import RiaPicker from "./elements/ria-picker";
  import Separator from "../common/separator";
  import { getFullname } from "@/helpers/skiersHelpers";
  import { translate } from '@/helpers/translationsHelpers.js';
  import { DateTime } from "luxon";
  import { isSafariBrowser } from '@/helpers/deviceHelpers.js';

  export default {
    name: "form-skier",
    components:{
      Card, RiaInput, RiaInputAction, RiaCheckboxExtra, RiaPicker, Separator
    },
    props: {
      participant: Object,
      index: Number,
    },
    data: function() {
      return {
        isSafariBrowser:false,
        isParticipantPickerVisible: false,
        personSelected: null,
        keycard_id: null,
        keycard_number: null,
        fieldsToValidate: [],
        inputs: []
      }
    },
    computed: {
      ...mapGetters("auth", ["isAnonymous"]),
      ...mapGetters("resort", ["selectedProduct", "selectedCatalog"]),
      ...mapGetters("order", ["details", "detailsSelected"]),
      ...mapGetters("customer", ["customer", "participantById"]),
      ...mapGetters("prices", ["extraPrice", "hasExtra", "loadedExtras"]),
      isValid: function() {
        return this.fieldsToValidate.every((field) => this.participant[field]) 
      },
      availablePeople() {
        return this.customer.people.filter((person) => {
          return !this.detailsSelected.includes(person.id)
        })
      },
      availablePeopleOptions() {
        return this.availablePeople.map((participant) => ({ name: getFullname(participant), value: participant.id }))
      },
      participantId() {
        return this.details[this.index].participant_id
      },
      availableExtras() {
        if (this.loadedExtras) {
          const availableExtras = this.selectedProduct.extras
            .filter((currentExtra) => 
              currentExtra.active && 
              this.hasExtra(currentExtra.id, this.participantId) && 
              typeof this.extraPrice(currentExtra.id, this.participantId) === 'number'
            )
            .sort((a,b) => {return a.id - b.id})

          return availableExtras;
        }

        return []
      },
      birthdateValidation: function() {
        const now = DateTime.local()
        const today = now.toFormat('yyyy-MM-dd');
        const hundredYearsBefore = now.minus({years: 100 }).toFormat('yyyy-MM-dd');
        let validationClauses = `required|before:${today}|after:${hundredYearsBefore}`;
        if(this.isSafariBrowser){
          validationClauses += '|date:YYYY-MM-DD';
        }
        return validationClauses;
      }
    },
    created() {
      const fieldsToValidate = ['firstname', 'lastname','birthdate']

      if (this.selectedProduct.requirements.photo) fieldsToValidate.push('photo')
      if (this.selectedProduct.requirements.keycard){
        this.keycard_id = this.details[this.index].keycard.id;
        this.keycard_number = this.details[this.index].keycard.number;
        fieldsToValidate.push('keycard')
      }
      this.fieldsToValidate = fieldsToValidate
      this.isSafariBrowser = isSafariBrowser();


    },
    mounted() {
      this.inputs = Object.values(this.$refs)
    },
    methods: {
      ...mapMutations('order', ['SET_DETAIL', 'SET_DETAIL_FIELD']),
      getParticipantType(participantId) {
        return this.selectedCatalog.participants.find((participant) => participant.id === participantId)
      },
      getFullname(skier) {
        return getFullname(skier)
      },
      openParticipantPicker() {
        this.isParticipantPickerVisible = true;
      },
      closeParticipantPicker() {
        this.isParticipantPickerVisible = false;
      },
      goToPhoto() {
        this.$router.push({ name: 'Photo',params: { participantIndex: `${this.index}` }})
      },
      goToKeycard() {
        this.$router.push({ name: 'Keycard',params: { participantIndex: `${this.index}` }})
      },
      changeValue: function({ field, value, hasErrors }) {
        if (!hasErrors) {
          this.SET_DETAIL_FIELD({ index: this.index, field, value })
        }
      },
      changeParticipant: function(participantId) {
        this.SET_DETAIL({
          index: this.index,
          participant: {
            ...this.details[this.index],
            ...this.participantById(participantId),
            person_id: participantId
          }
        })
      },
      changeExtra: function(extra) {
        const value = extra.enabled
          ? this.details[this.index].extras.concat(extra.id)
          : this.details[this.index].extras.filter((ext) => ext !== extra.id)

        this.SET_DETAIL_FIELD({ index: this.index, field: 'extras', value })
      },
      enabledExtra(extraId) {
        return this.details[this.index].extras.includes(extraId)
      },
      translate(object, field) {
        return translate(object, field)
      },
      checkValidations() {
        this.inputs.forEach(input => {
          if (input.isValid() instanceof Promise) {
            input.isValid().then((data) => input.showError = !data)
          } else {
            input.showError = !input.isValid()
          }
        })
      }
    }
  };
</script>
